import { memo, useEffect, useState } from "react";
import CodeEntry from "./CodeEntry";
import StringEntry from "./StringEntry";
import ArrayEntry from "./ArrayEntry";
import PeriodEntry from "./PeriodEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const displayStringFromFHIRHumanName = (humanName) => {
    if (humanName === undefined || humanName === null || humanName === '') {
        return ""
    }
  if (typeof humanName === "object") {
    try {
        if (humanName.text) {
            return `${humanName.text}`;
        }
        if (typeof humanName.given === 'string' || typeof humanName.prefix === 'string' || typeof humanName.suffix === 'string') {
            return "[ERROR: HumanName data not interpretable. One of the contained elements is in string form instead of array.]";
        }
        let prefixDisplay = "";
        let givenDisplay = "";
        let suffixDisplay = "";
        let familyDisplay = humanName.family || "";
        if (humanName.prefix?.length > 0) {
            for (const prefixItem of humanName.prefix) {
                prefixDisplay = prefixDisplay + prefixItem + " ";
            }
        }
        if (humanName.given?.length > 0) {
            for (const givenName of humanName.given) {
                givenDisplay = givenDisplay + givenName + " ";
            }
        }
        if (humanName.suffix?.length > 0) {
            for (const suffixItem of humanName.suffix) {
                suffixDisplay = suffixDisplay + ", " + suffixItem;
            }
        }
        let nameDisplay = prefixDisplay + givenDisplay + familyDisplay + suffixDisplay;
        if (nameDisplay && typeof nameDisplay === 'string') {
            return nameDisplay;
        }
    } catch (e) { console.log(e); }
  }
    if (typeof humanName === 'string') {
        return `${humanName} [ERROR: HumanName data not in expected HumanName datatype.]`;
    }
    return "[ERROR: HumanName data not interpretable.]";
}

const humanNameDotUseValues = ["usual", "official", "temp", "nickname", "anonymous", "old", "maiden"];
const HumanNameEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  let startingHumanName = { "use": "", "text": "", "family": "", "given": [], "prefix": [], "suffix": [], "period": "" };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingHumanName.extension = startingValue.extension; }
    if (startingValue.use) { startingHumanName.use = startingValue.use; }
    if (startingValue.text) { startingHumanName.text = startingValue.text; }
    if (startingValue.family) { startingHumanName.family = startingValue.family; }
    if (startingValue.given) { startingHumanName.given = startingValue.given; }
    if (startingValue.prefix) { startingHumanName.prefix = startingValue.prefix; }
    if (startingValue.suffix) { startingHumanName.suffix = startingValue.suffix; }
    if (startingValue.period) { startingHumanName.period = startingValue.period; }
  }

  const [humanNameState, setHumanNameState] = useState(JSON.parse(JSON.stringify(startingHumanName || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(humanNameState).length) {
      let newHumanName = {};
      if (humanNameState.extension) { newHumanName.extension = humanNameState.extension; }
      if (humanNameState.use) { newHumanName.use = humanNameState.use; }
      if (humanNameState.text) { newHumanName.text = humanNameState.text; }
      if (humanNameState.family) { newHumanName.family = humanNameState.family; }
      if (humanNameState.given !== null && humanNameState.given !== undefined &&
        Array.isArray(humanNameState.given) && humanNameState.given.length !== 0 &&
        !(humanNameState.given.length === 1 && humanNameState.given[0] === "")) {
        newHumanName.given = humanNameState.given;
      }
      if (humanNameState.prefix !== null && humanNameState.prefix !== undefined &&
        Array.isArray(humanNameState.prefix) && humanNameState.prefix.length !== 0 &&
        !(humanNameState.prefix.length === 1 && humanNameState.prefix[0] === "")) {
        newHumanName.prefix = humanNameState.prefix;
      }
      if (humanNameState.suffix !== null && humanNameState.suffix !== undefined &&
        Array.isArray(humanNameState.suffix) && humanNameState.suffix.length !== 0 &&
        !(humanNameState.suffix.length === 1 && humanNameState.suffix[0] === "")) {
        newHumanName.suffix = humanNameState.suffix;
      }
      if (humanNameState.period && Object.keys(humanNameState.period).length) { newHumanName.period = humanNameState.period; }
      if (Object.keys(newHumanName).length === 0) {
        newHumanName = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newHumanName } });
    }
  }), [humanNameState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRHumanName(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <div>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <CodeEntry elementName='use' fieldLabel='Use'
          startingValue={humanNameState.use} setResourceState={setHumanNameState} dataEntryStyle='dropdown'
          allowedValues={humanNameDotUseValues} />
        <StringEntry elementName='text' fieldLabel='Text'
          startingValue={humanNameState.text} setResourceState={setHumanNameState} />
        <StringEntry elementName='family' fieldLabel='Family'
          startingValue={humanNameState.family} setResourceState={setHumanNameState} />
        <ArrayEntry datatype='string' elementName='given'
          fieldLabel='Given' startingValue={humanNameState.given} setResourceState={setHumanNameState} />
        <ArrayEntry datatype='string' elementName='prefix'
          fieldLabel='Prefix' startingValue={humanNameState.prefix} setResourceState={setHumanNameState} />
        <ArrayEntry datatype='string' elementName='suffix'
          fieldLabel='Suffix' startingValue={humanNameState.suffix} setResourceState={setHumanNameState} />
        <PeriodEntry elementName='period' fieldLabel='Period' startCollapsed={true}
          startingValue={humanNameState.period} setResourceState={setHumanNameState} />
      </div>
    </div>
  }
});

export default HumanNameEntry;
export { displayStringFromFHIRHumanName };